import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Dialog } from 'react-toolbox';
import { Button, IconButton } from 'react-toolbox/lib/button';
import MessageBox from 'components/MessageBox';
import { normalizePayRateLineItemOptions } from '../../../../../../../utils/lineItemUtils';
import modalS from 'modal.scss';

class CustomLaborSheetModal extends Component {
  state = {
    errorMessage: '',
    lineItems: [{ uid: 0, line_item_id: '', quantity: 1 }],
  }

  getOptionById = lineItemId => this.lineItemOptionsList()
    .find(option => String(option.value) === String(lineItemId));

  lineItemOptionsList = () =>
    normalizePayRateLineItemOptions(this.props.lineItemOptions, this.props.laborSheets);

  lineItemUid = 1

  handleLineItemChange = (idx, attr) => (event) => {
    const value = event && event.target ? event.target.value : event;
    this.setState({
      lineItems: this.state.lineItems.map((item, itemIdx) =>
        (itemIdx === idx ? { ...item, [attr]: value } : item)),
    });
  }

  addLineItem = () => {
    this.setState({
      lineItems: [...this.state.lineItems, {
        uid: this.lineItemUid,
        line_item_id: '',
        quantity: 1,
      }],
    });
    this.lineItemUid += 1;
  }

  removeLineItem = (idx) => {
    if (this.state.lineItems.length === 1) {
      this.setState({ lineItems: [{ uid: 0, line_item_id: '', quantity: 1 }] });
      return;
    }
    this.setState({ lineItems: this.state.lineItems.filter((_, itemIdx) => itemIdx !== idx) });
  }

  buildContractorLineItems = laborSheetId =>
    this.state.lineItems
      .map((item) => {
        const option = this.getOptionById(item.line_item_id);
        const quantity = Number(item.quantity);
        if (!option || !quantity || quantity <= 0) return null;
        return {
          labor_sheet_id: laborSheetId,
          line_item_id: option.value,
          quantity,
          price: option.price,
        };
      })
      .filter(Boolean);

  handleSubmit = () => {
    const { phase, laborSheets } = this.props;
    const hasLaborSheet = (laborSheets || []).some(sheet => sheet && sheet.id > 0);
    if (hasLaborSheet) {
      this.props.onClose();
      this.props.navigate(`/phases/${phase.id}/labor-product-accounting/laborsheet`);
      return;
    }

    const appointment = (phase.appointments || [])[0];
    const laborSheet = appointment ? { appointment_id: appointment.id } : {};
    this.props.createPhaseItem(phase.id, laborSheet, 'laborSheets')
      .then((action) => {
        if (action && action.type === 'CREATE_PHASE_ITEMS_SUCCESS') {
          const createdLaborSheet = action.line_item;
          const contractorLineItems = this.buildContractorLineItems(createdLaborSheet.id);
          if (!contractorLineItems.length) {
            this.setState({ errorMessage: '' });
            this.props.onClose();
            this.props.navigate(`/phases/${phase.id}/labor-product-accounting/laborsheet`);
            return;
          }
          Promise.all(contractorLineItems.map(lineItem =>
            this.props.createPhaseItem(phase.id, lineItem, 'contractorLineItems')))
            .then((results) => {
              const failed = results.some(result => !result || result.type !== 'CREATE_PHASE_ITEMS_SUCCESS');
              if (failed) {
                this.setState({ errorMessage: 'Labor sheet created, but some line items failed to save.' });
                return;
              }
              this.setState({ errorMessage: '' });
              this.props.onClose();
              this.props.navigate(`/phases/${phase.id}/labor-product-accounting/laborsheet`);
            });
          return;
        }
        this.setState({ errorMessage: 'Failed to create Custom Labor Sheet.' });
      });
  }

  renderLineItemsEditor = () => (<div>
    <p className={modalS.text}>Optional: add line items before creating.</p>
    {this.state.lineItems.map((lineItem, idx) => {
      const option = this.getOptionById(lineItem.line_item_id);
      const price = option ? Number(option.price || 0) : 0;
      const quantity = Number(lineItem.quantity || 0);
      const subtotal = price * quantity;
      return (<div key={lineItem.uid}>
        <div>
          <label htmlFor={`item-select-${idx}`}>Item</label>
          <select
            id={`item-select-${idx}`}
            value={lineItem.line_item_id}
            onChange={this.handleLineItemChange(idx, 'line_item_id')}
          >
            <option value="">Select item</option>
            {this.lineItemOptionsList().map(optionItem => (
              <option key={optionItem.value} value={optionItem.value}>
                {optionItem.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`item-qty-${idx}`}>Quantity</label>
          <input
            id={`item-qty-${idx}`}
            type="number"
            min="1"
            value={lineItem.quantity}
            onChange={this.handleLineItemChange(idx, 'quantity')}
          />
        </div>
        <div>
          <strong>Description:</strong> {option ? option.label : '-'}
        </div>
        <div>
          <strong>Cost:</strong> ${price.toFixed(2)}
        </div>
        <div>
          <strong>Subtotal:</strong> ${subtotal.toFixed(2)}
        </div>
        <Button
          label="Remove"
          flat
          onClick={() => this.removeLineItem(idx)}
        />
      </div>);
    })}
    <Button label="Add Line Item" flat onClick={this.addLineItem} />
  </div>)

  render = () => {
    const { active, onClose } = this.props;
    const { errorMessage } = this.state;
    return (<Dialog
      active={active}
      className={modalS.modal}
      type="fullscreen"
      onEscKeyDown={onClose}
      onOverlayClick={onClose}
    >
      {errorMessage && <MessageBox error message={errorMessage} />}
      <IconButton
        icon="close"
        className={modalS.close}
        onClick={onClose}
        tabIndex={-1}
      />
      <h5 className={modalS.title}>Create Custom Labor Sheet</h5>
      <p className={modalS.text}>
        Confirm to create a custom labor sheet now.
        You can continue and add labor line items after creation.
      </p>
      {this.renderLineItemsEditor()}
      <Button
        label="Create"
        flat inverse ripple
        className={modalS.submit}
        onClick={this.handleSubmit}
      />
    </Dialog>);
  }
}

CustomLaborSheetModal.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  phase: PropTypes.object.isRequired,
  laborSheets: PropTypes.array,
  lineItemOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  createPhaseItem: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

CustomLaborSheetModal.defaultProps = {
  laborSheets: [],
  lineItemOptions: [],
};

export default CustomLaborSheetModal;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/phaseActionMenu/customLaborSheetModal.jsx